import { column, Css, GridColumn, GridDataRow, GridTable, numericColumn, simpleHeader } from "@homebound/beam";
import { priceCell } from "src/components/gridTableCells";
import { PlanBasedLineItemsTab_LineItemFragment } from "src/routes/projects/commitments/components/PlanBasedLineItemsTab.generated";

type PlanBasedBidLinesSectionProps = { lineItems: PlanBasedLineItemsTab_LineItemFragment[] };

export function PlanBasedBidLinesSection({ lineItems }: PlanBasedBidLinesSectionProps) {
  return (
    <section data-testid="planBasedBidLinesSection">
      <h2 css={Css.mdSb.mt3.mb1.$}>Cost Lines</h2>
      <GridTable columns={columns} rows={createRows(lineItems)} />
    </section>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = {
  kind: "data";
  data: {
    option: string;
    dimensions: string;
    committedInCents: number;
    billedInCents: number;
    unbilledInCents: number;
    paidInCents: number;
    pendingInCents: number;
  };
};
type Row = HeaderRow | DataRow;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: () => "Option", data: ({ option }) => option }),
  column<Row>({ header: () => "Dimensions", data: ({ dimensions }) => dimensions }),
  numericColumn<Row>({
    header: () => "Committed",
    data: ({ committedInCents }) => priceCell({ id: "committed", valueInCents: committedInCents }),
  }),
  numericColumn<Row>({
    header: () => "Billed",
    data: ({ billedInCents }) => priceCell({ id: "billed", valueInCents: billedInCents }),
  }),
  numericColumn<Row>({
    header: () => "Unbilled",
    data: ({ unbilledInCents }) => priceCell({ id: "unbilled", valueInCents: unbilledInCents }),
  }),
  numericColumn<Row>({
    header: () => "Paid to Date",
    data: ({ paidInCents }) => priceCell({ id: "paid", valueInCents: paidInCents }),
  }),
  numericColumn<Row>({
    header: () => "Pending",
    data: ({ pendingInCents }) => priceCell({ id: "pending", valueInCents: pendingInCents }),
  }),
];

function createRows(lineItems: PlanBasedLineItemsTab_LineItemFragment[]): GridDataRow<Row>[] {
  const dataRows = lineItems
    .filter((cli) => cli.planBasedLineItem)
    .map((cli) => {
      const { groupLine } = cli.planBasedLineItem!;
      return {
        kind: "data" as const,
        id: cli.planBasedLineItem!.id,
        data: {
          option: groupLine.group.displayName,
          dimensions: groupLine.options.map((o) => o.name).join(" "),
          committedInCents: cli.committedInCents,
          billedInCents: cli.billedInCents ?? 0,
          unbilledInCents: cli.unbilledInCents,
          paidInCents: cli.paidInCents ?? 0,
          pendingInCents: cli.pendingBilledInCents,
        },
      };
    });
  return [simpleHeader, ...dataRows];
}
